import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, Search } from 'lucide-react';
import type { Event } from '../../types';

interface EventSearchSelectProps {
  value: number;
  onChange: (eventId: number) => void;
  error?: string | undefined;
  events: Event[];
  placeholder?: string;
  required?: boolean;
  /**
   * When provided, the search term is forwarded here (debounced ~300ms) so the
   * caller can query the server, instead of filtering `events` client-side.
   */
  onSearchChange?: (term: string) => void;
}

const EventSearchSelect: React.FC<EventSearchSelectProps> = ({
  value,
  onChange,
  error,
  events,
  placeholder = "Zoek of selecteer een evenement",
  required = false,
  onSearchChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [selectedEventCache, setSelectedEventCache] = useState<Event | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When a server-side search is wired up, `events` already reflects the
  // search results — only filter client-side as a fallback.
  const filteredEvents = useMemo(() => {
    if (onSearchChange) return events;
    if (!searchTerm.trim()) return events;
    return events.filter(event =>
      event.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [events, searchTerm, onSearchChange]);

  // Get selected event — fall back to a cached reference so the label keeps
  // showing correctly even after a new search narrows the `events` list.
  const selectedEvent = events.find(event => event.id === value)
    || (selectedEventCache?.id === value ? selectedEventCache : undefined);

  useEffect(() => {
    const found = events.find(event => event.id === value);
    if (found) setSelectedEventCache(found);
  }, [events, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up any pending debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleInputFocus = () => {
    setShowDropdown(true);
    setFocusedIndex(-1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);
    setFocusedIndex(-1);

    if (onSearchChange) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearchChange(term);
      }, 300);
    }
  };

  const handleEventSelect = (event: Event) => {
    onChange(event.id);
    setSelectedEventCache(event);
    setSearchTerm('');
    setShowDropdown(false);
    setFocusedIndex(-1);
    inputRef.current?.blur();

    // Restore the default (unfiltered) list now that a choice has been made.
    if (onSearchChange) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      onSearchChange('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setShowDropdown(true);
        setFocusedIndex(-1);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredEvents.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredEvents.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && filteredEvents[focusedIndex]) {
          handleEventSelect(filteredEvents[focusedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setFocusedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={showDropdown ? searchTerm : (selectedEvent ? selectedEvent.title : '')}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`sf-input pr-10 ${error ? '!border-neon-pink/60' : ''}`}
          required={required}
          autoComplete="off"
        />

        {/* Icons */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3.5 pointer-events-none">
          {showDropdown ? (
            <Search className="h-4 w-4 text-fg-faint" />
          ) : (
            <ChevronDown className="h-4 w-4 text-fg-faint" />
          )}
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-10 w-full mt-1.5 bg-surface border border-white/[.09] rounded-field shadow-floating max-h-60 overflow-auto">
          {filteredEvents.length > 0 ? (
            <>
              {filteredEvents.map((event, index) => (
                <div
                  key={event.id}
                  className={`flex items-center space-x-3 p-3 cursor-pointer hover:bg-white/[.04] ${
                    index === focusedIndex ? 'bg-white/[.06]' : ''
                  } ${index === 0 ? 'rounded-t-field' : ''} ${
                    index === filteredEvents.length - 1 ? 'rounded-b-field' : ''
                  }`}
                  onClick={() => handleEventSelect(event)}
                >
                  <div className="flex-shrink-0">
                    <Calendar className="h-4 w-4 text-neon-cyan" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-fg truncate">
                      {event.title}
                    </p>
                    <p className="text-sm text-fg-muted truncate">
                      {formatDate(event.startDate)} • {event.location}
                    </p>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="p-4 text-center text-fg-muted">
              <p className="text-sm italic">Geen evenementen gevonden</p>
              {searchTerm && (
                <p className="text-xs mt-1 text-fg-meta">
                  Probeer een andere zoekterm
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-sm text-neon-pink-soft mt-1">{error}</p>
      )}
    </div>
  );
};

export default EventSearchSelect;